import { useAuth } from '@cannect/hooks/AuthContext'
import { useCannectAssistant } from '@cannect/hooks/useCannectAssistant'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import useQueryString from '@cannect/hooks/useQueryString'
import useSignedUser from '@cannect/hooks/useSignedUser'
import { useTokenApi } from '@cannect/hooks/useTokenApi'
import { TLinkItem } from '@cannect/new-components/molecules/LinkItem/types'
import { usePublicRender } from '@cannect/utils/renderActions'
import { deleteUser } from 'hooks/useSessionUser'
import { useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'
import api, { logoutServerSession } from 'services/api'
import { getAccessToken } from '../../../services/authToken'

export const useHeader = () => {
  const history = useHistory()
  const location = useLocation()
  const { filters } = useQueryString()
  const { signedUser } = useSignedUser()
  const { deleteTokenApi, tokenApi } = useTokenApi()
  const { prescription, setStep, loading } = useCannectAssistant()
  const { getRoles } = useAuth()
  const { getLastOrder, lastOrderCreated, loadingCupon, clearOrderData, clearCart } = usePrepareCart()
  const { isPublic: isPublicFlow } = usePublicRender()

  const signedUserName = useMemo(() => {
    const name = signedUser?.preferred_name || signedUser?.name || ''
    return name.split(' ')[0]
  }, [signedUser])

  const isAssistantFlow = useMemo(() => location.pathname.startsWith('/produtos_assistente'), [location])

  const isDigitalJourneyFlow = useMemo(() => location.pathname.includes('jornada-digital'), [location?.pathname])

  const getProductQuantity = (products: any) => {
    return (
      // @ts-expect-error - we need to type products
      products?.reduce((acc, curr) => {
        const itemQuantity = curr.quantity || curr.quantity_item || 0
        return acc + itemQuantity
      }, 0) || 0
    )
  }

  const cartProductsQuantity = useMemo(() => {
    if (isAssistantFlow) {
      return getProductQuantity(prescription?.products)
    }

    return getProductQuantity(lastOrderCreated?.items)
  }, [isAssistantFlow, prescription?.products, lastOrderCreated?.items])

  const isLogged = useMemo(() => !!tokenApi, [tokenApi])

  const prescriberHasLogo = useMemo(
    () => prescription?.prescriber?.profilePicture,
    [prescription?.prescriber?.profilePicture]
  )

  const handleLogout = () => {
    try {
      // Limpa dados de carrinho e pedido antes de sair
      clearCart()
      clearOrderData()

      localStorage.removeItem('association_order_id')
    } catch (error) {
      console.error('Error clearing cart:', error)
    }
    // Revoga a família do refresh no servidor e limpa o cookie httpOnly
    // (fire-and-forget: a rota é baseada no próprio cookie, não no access).
    logoutServerSession()
    deleteTokenApi()
    deleteUser()
    getRoles()
    history.push('/AreaAssociados')
  }

  const handleAssistantContinue = () => {
    const validateStep = prescriberHasLogo ? 2 : 1
    setStep((step) => step + validateStep)
    let url = '/assistente'
    if (filters.template) {
      url += `?template=${filters.template}`
    }
    history.push(url)
  }

  const handlePrescriberAccess = async () => {
    let assistantToken: string | undefined
    try {
      const cannectToken = getAccessToken()
      if (cannectToken) {
        const { data } = await api.post(
          '/generate-assistant-token',
          {},
          { headers: { Authorization: `Bearer ${cannectToken}` } }
        )
        assistantToken = data?.token
      }
    } catch (err) {
      return
    }

    const assistantBaseUrl = import.meta.env.VITE_APP_HEALTH_PROFESSIONAL_BASE_URL || 'http://localhost:3003/'
    if (assistantToken) {
      window.open(`${assistantBaseUrl}#/login?token=${assistantToken}`, '_blank')
    } else {
      window.open(`${assistantBaseUrl}#/login`, '_blank')
    }
  }

  useEffect(() => {
    if (tokenApi) {
      getLastOrder()
    }
  }, [tokenApi])

  const menuConfig: TLinkItem[] = [
    {
      title: 'Produtos',
      path: '/produtos'
      // disabled: !isLogged, // Removido para permitir acesso sem login
      // columns: [ // Dropdown comentado - pode ser reativado se necessário
      //   {
      //     title: '',
      //     items: [
      //       { title: 'Cannabis medicinal Importado', path: _buildFilterUrl(_ProductFilters.IMPORTED_ALL) },
      //       // { title: 'Produtos com THC', path: _buildFilterUrl(_ProductFilters.IMPORTED_WITH_THC) },
      //       // { title: 'Produtos sem THC', path: _buildFilterUrl(_ProductFilters.IMPORTED_WITHOUT_THC) },
      //       { title: 'Óleos', path: _buildFilterUrl(_ProductFilters.IMPORTED_OILS) },
      //       { title: 'Gomas', path: _buildFilterUrl(_ProductFilters.IMPORTED_GUMS) },
      //       { title: 'Tópicos', path: _buildFilterUrl(_ProductFilters.IMPORTED_TOPICALS) },
      //       { title: 'Outros', path: _buildFilterUrl(_ProductFilters.IMPORTED_OTHERS) }
      //     ]
      //   },
      //   {
      //     title: '',
      //     items: [
      //       { title: 'Cannabis medicinal Farmácia', path: _buildFilterUrl(_ProductFilters.PHARMACY_ALL) },
      //       // { title: 'Produtos com THC', path: _buildFilterUrl(_ProductFilters.PHARMACY_WITH_THC) },
      //       // { title: 'Produtos sem THC', path: _buildFilterUrl(_ProductFilters.PHARMACY_WITHOUT_THC) },
      //       { title: 'Óleos', path: _buildFilterUrl(_ProductFilters.PHARMACY_OILS) },
      //       { title: 'Outros', path: _buildFilterUrl(_ProductFilters.PHARMACY_OTHERS) }
      //     ]
      //   },
      //   {
      //     title: '',
      //     items: [
      //       { title: 'Suplementos', path: _buildFilterUrl(_ProductFilters.SUPPLEMENTS_ALL) },
      //       { title: 'PEA', path: _buildFilterUrl(_ProductFilters.SUPPLEMENTS_PEA) },
      //       { title: 'Curcumina', path: _buildFilterUrl(_ProductFilters.SUPPLEMENTS_CURCUMIN) },
      //       { title: 'Melatonina', path: _buildFilterUrl(_ProductFilters.SUPPLEMENTS_MELATONIN) },
      //       { title: 'Creatina', path: _buildFilterUrl(_ProductFilters.SUPPLEMENTS_CREATINE) },
      //       { title: 'Outros', path: _buildFilterUrl(_ProductFilters.SUPPLEMENTS_OTHERS) }
      //     ]
      //   },
      //   {
      //     title: '',
      //     items: [
      //       { title: 'Cogumelos funcionais', path: _buildFilterUrl(_ProductFilters.MUSHROOMS_ALL) },
      //       { title: 'Óleos', path: _buildFilterUrl(_ProductFilters.MUSHROOMS_OILS) },
      //       { title: 'Outros', path: _buildFilterUrl(_ProductFilters.MUSHROOMS_OTHERS) },
      //     ]
      //   }
      // ]
    },
    { title: 'Agendar Consulta', path: '/agendamento' },
    { title: 'Profissionais de saúde', path: '#', onClick: handlePrescriberAccess },
    {
      title: 'Parceiros e soluções',
      submenu: [
        { title: 'Seja nosso parceiro', path: '/area-parceiros' },
        { title: 'Operadoras de saúde', path: 'https://atendimento.cannect.life/operadoras-de-saude', external: true }
      ]
    },
    {
      title: 'Blog',
      path: !isLogged ? 'https://blog.cannect.life/' : `https://blog.cannect.life?user=${signedUserName}`,
      external: true
    }
  ]

  return {
    menuConfig,
    signedUserName,
    handleLogout,
    isLogged,
    cartProductsQuantity,
    isAssistantFlow,
    handleAssistantContinue,
    handlePrescriberAccess,
    isLoadingCartQuantity: loadingCupon || loading,
    isPublicFlow,
    isDigitalJourneyFlow
  }
}
